/**
 * Group Approvals React Query hooks.
 *
 * Day 14 §5.3.2 Group Approvals.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { groupApprovalsService } from "@/services/groupApprovalsService";
import type { ApprovalQueueFilters } from "@/types/group-approvals.types";

const NS = ["group-approvals"] as const;
export const queueKey = (filters: ApprovalQueueFilters = {}) =>
  [...NS, "queue", filters] as const;
export const thresholdKey = () => [...NS, "threshold"] as const;

export function useApprovalQueue(filters: ApprovalQueueFilters = {}) {
  return useQuery({
    queryKey: queueKey(filters),
    queryFn: () => groupApprovalsService.getQueue(filters),
    staleTime: 15_000, // queue is dynamic — refresh often
    refetchInterval: 30_000, // background poll
  });
}

export function useApprovalThreshold() {
  return useQuery({
    queryKey: thresholdKey(),
    queryFn: () => groupApprovalsService.getThreshold(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useApproveExpenditure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (expenditureId: string) =>
      groupApprovalsService.approve(expenditureId),
    onSuccess: () => qc.invalidateQueries({ queryKey: NS }),
  });
}

export function useRejectExpenditure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      expenditureId,
      reason,
    }: {
      expenditureId: string;
      reason: string;
    }) => groupApprovalsService.reject(expenditureId, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: NS }),
  });
}

export function useSetThreshold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threshold: number) =>
      groupApprovalsService.setThreshold(threshold),
    onSuccess: () => qc.invalidateQueries({ queryKey: NS }),
  });
}
